import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import { Icon, IconName } from '../../atoms/Icon'
import { Badge } from '../../atoms/Badge'
import { Loading } from '../../atoms/Loading'
import { LandAnalysisCard } from '../LandAnalysisCard'
import { BuildingPermitDetailContentProps } from './BuildingPermitDetail.types'
import { MAPBOX_TOKEN } from '../../../config/constants'
import * as S from './BuildingPermitDetail.styles'
import { PrintModeWrapper } from './BuildingPermitDetailContent.styles'

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN

// Icon mapping for RegBL flags - same as list view
const regblFlagIcons: Record<string, IconName> = {
  regbl_change_of_use: 'arrow-left-right',
  regbl_interior_transformation: 'home',
  regbl_other_transformation: 'tool',
  regbl_heated_extension: 'flame',
  regbl_unheated_extension: 'box',
  regbl_energetic_renovation: 'leaf',
  regbl_heating_system_renovation: 'gauge',
  regbl_solar_thermal_installation: 'sun',
  regbl_solar_photovoltaic_installation: 'zap',
}

// Helper functions for status chip variants and icons
const getStatusVariant = (
  status: string
): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' => {
  const upperStatus = status.toUpperCase()
  switch (upperStatus) {
    case 'REALISE':
    case 'ACCEPTE':
      return 'success'
    case 'CHANTIER':
    case 'RECOURS':
      return 'warning'
    case 'REFUSE':
    case 'CADUQUE':
      return 'danger'
    case 'TERMINE':
      return 'primary'
    default:
      return 'secondary'
  }
}

const getStatusIcon = (status: string): IconName => {
  const upperStatus = status.toUpperCase()
  switch (upperStatus) {
    case 'REALISE':
    case 'ACCEPTE':
    case 'TERMINE':
      return 'check'
    case 'CHANTIER':
    case 'RECOURS':
      return 'clock'
    case 'REFUSE':
    case 'CADUQUE':
    case 'ABANDONNE':
      return 'x'
    default:
      return 'info'
  }
}

const getStatusKey = (status: string): string => {
  const upperStatus = status.toUpperCase()
  switch (upperStatus) {
    case 'REALISE':
      return 'realized'
    case 'ACCEPTE':
      return 'accepted'
    case 'CHANTIER':
      return 'construction'
    case 'RECOURS':
      return 'appeal'
    case 'REFUSE':
      return 'refused'
    case 'CADUQUE':
      return 'expired'
    case 'TERMINE':
      return 'completed'
    case 'ARCHIVE':
      return 'archived'
    case 'ABANDONNE':
      return 'abandoned'
    default:
      return status.toLowerCase()
  }
}

const getClassificationVariant = (
  classification: string
): 'success' | 'warning' | 'primary' | 'secondary' => {
  const lowerClass = classification.toLowerCase()
  if (lowerClass === 'new construction') return 'success'
  if (lowerClass === 'transformation') return 'primary'
  if (lowerClass === 'demolition') return 'warning'
  return 'secondary'
}

const getClassificationIcon = (classification: string): IconName => {
  const lowerClass = classification.toLowerCase()
  if (lowerClass === 'new construction') return 'building-2'
  if (lowerClass === 'transformation') return 'wrench'
  if (lowerClass === 'demolition') return 'hammer'
  return 'building'
}

export const BuildingPermitDetailContent: React.FC<
  BuildingPermitDetailContentProps
> = ({
  permit,
  loading = false,
  error = null,
  formatAddress,
  getTotalLandArea,
  getPrimaryOfficialId,
  getActiveRegblFlags,
  getParticipantsByRoleGrouped,
  formatLandArea,
  resolvedLands,
  landsLoading,
  onViewLand,
  printMode = false,
}) => {
  const { t } = useTranslation()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null)

  // Refs for scroll targets (only used in non-print mode)
  const permitInfoRef = useRef<HTMLDivElement>(null)
  const classificationRef = useRef<HTMLDivElement>(null)
  const participantsRef = useRef<HTMLDivElement>(null)
  const landParcelsRef = useRef<HTMLDivElement>(null)
  const featuresRef = useRef<HTMLDivElement>(null)

  // Scroll to section handler
  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (!printMode) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !permit?.geo_center || !MAPBOX_TOKEN) return

    // Parse geo_center - Elasticsearch geo_point can be in different formats
    let lon: number | undefined
    let lat: number | undefined

    if (typeof permit.geo_center === 'string') {
      const parts = permit.geo_center.split(',')
      if (parts.length === 2) {
        const val1 = parseFloat(parts[0])
        const val2 = parseFloat(parts[1])

        if (val1 >= 45 && val1 <= 48 && val2 >= 5 && val2 <= 11) {
          lat = val1
          lon = val2
        } else if (val2 >= 45 && val2 <= 48 && val1 >= 5 && val1 <= 11) {
          lat = val2
          lon = val1
        } else {
          lat = val1
          lon = val2
        }
      }
    } else if (Array.isArray(permit.geo_center)) {
      lon = permit.geo_center[0]
      lat = permit.geo_center[1]
    } else if (
      permit.geo_center &&
      typeof permit.geo_center === 'object' &&
      'lat' in permit.geo_center &&
      'lon' in permit.geo_center
    ) {
      lat = permit.geo_center.lat
      lon = permit.geo_center.lon
    }

    if (
      typeof lon !== 'number' ||
      typeof lat !== 'number' ||
      isNaN(lon) ||
      isNaN(lat)
    ) {
      console.warn('Invalid geo_center coordinates:', permit.geo_center)
      return
    }

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }

    try {
      const map = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [lon, lat],
        zoom: 15,
        interactive: !printMode,
        attributionControl: false,
      })

      map.on('load', () => {
        new mapboxgl.Marker({ color: '#2563eb' })
          .setLngLat([lon, lat])
          .addTo(map)
      })

      mapInstanceRef.current = map
    } catch (error) {
      console.error('Error initializing map:', error)
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [permit, printMode])

  // Format date helper
  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return '—'
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // Get status badge configuration
  const getStatusBadge = (status: string | undefined) => {
    if (!status) return null

    const upperStatus = status.toUpperCase()
    let variant: 'success' | 'warning' | 'danger' | 'primary' | 'secondary' =
      'secondary'
    let labelKey = 'detail.status.realized'

    switch (upperStatus) {
      case 'REALISE':
        variant = 'success'
        labelKey = 'detail.status.realized'
        break
      case 'ACCEPTE':
        variant = 'success'
        labelKey = 'detail.status.accepted'
        break
      case 'CHANTIER':
        variant = 'warning'
        labelKey = 'detail.status.construction'
        break
      case 'RECOURS':
        variant = 'warning'
        labelKey = 'detail.status.appeal'
        break
      case 'REFUSE':
        variant = 'danger'
        labelKey = 'detail.status.refused'
        break
      case 'CADUQUE':
        variant = 'danger'
        labelKey = 'detail.status.expired'
        break
      case 'TERMINE':
        variant = 'primary'
        labelKey = 'detail.status.completed'
        break
      case 'ARCHIVE':
        variant = 'secondary'
        labelKey = 'detail.status.archived'
        break
      case 'ABANDONNE':
        variant = 'secondary'
        labelKey = 'detail.status.abandoned'
        break
      default:
        return (
          <Badge variant={variant} size="small">
            {status}
          </Badge>
        )
    }

    return (
      <Badge variant={variant} size="small">
        {t(`buildingPermits.${labelKey}`)}
      </Badge>
    )
  }

  if (loading) {
    return (
      <PrintModeWrapper $printMode={printMode}>
        <S.LoadingState>
          <Loading />
          <span>{t('buildingPermits.detail.loading')}</span>
        </S.LoadingState>
      </PrintModeWrapper>
    )
  }

  if (error) {
    return (
      <PrintModeWrapper $printMode={printMode}>
        <S.ErrorState>
          <h3>{t('buildingPermits.detail.error')}</h3>
          <p>{error}</p>
        </S.ErrorState>
      </PrintModeWrapper>
    )
  }

  if (!permit) {
    return null
  }

  // Calculate metrics for header
  const landArea = getTotalLandArea ? getTotalLandArea(permit) : undefined
  const participantsCount = permit?.participants?.length || 0
  const featuresCount = permit?.features?.length || 0

  // Get official ID for title
  const officialId = getPrimaryOfficialId
    ? getPrimaryOfficialId(permit)
    : permit?.id

  // Get address for subtitle
  const address = formatAddress ? formatAddress(permit) : '—'

  // Get status
  const status = permit?.official_final_decision || permit?.latest_status_db

  // Get active RegBL flags
  const regblFlags = getActiveRegblFlags ? getActiveRegblFlags(permit) : []

  // Get participants grouped by role
  const participantsByRole = getParticipantsByRoleGrouped
    ? getParticipantsByRoleGrouped(permit)
    : {}

  return (
    <PrintModeWrapper $printMode={printMode}>
      {/* Permit Info Section - Title, Address, Metrics, Map */}
      <S.PermitInfoSection>
        <S.PermitInfoMainContainer>
          <S.PermitInfoContent>
            <S.DetailTitle>
              <Icon name="file-text" size="small" />
              {t('buildingPermits.detail.title', { id: officialId })}
              {status && getStatusBadge(status)}
            </S.DetailTitle>
            <S.DetailSubtitle>
              <Icon name="map-pin" size="small" />
              {address}
              {permit?.inquiry_start_date && (
                <>
                  {' • '}
                  {t('buildingPermits.detail.inquiry')}{' '}
                  {formatDate(permit.inquiry_start_date)}
                </>
              )}
              <span style={{ color: 'white' }}>{permit.id}</span>
            </S.DetailSubtitle>

            {/* Quick Summary Chips */}
            <S.QuickSummaryChips>
              {/* Status */}
              {status && (
                <S.SummaryChip
                  $variant={getStatusVariant(status)}
                  onClick={() => scrollToSection(permitInfoRef)}
                >
                  <Icon name={getStatusIcon(status)} size="small" />
                  {t(`buildingPermits.detail.status.${getStatusKey(status)}`)}
                </S.SummaryChip>
              )}

              {/* Classification */}
              {permit.regbl_classification_general && (
                <S.SummaryChip
                  $variant={getClassificationVariant(
                    permit.regbl_classification_general
                  )}
                  onClick={() => scrollToSection(classificationRef)}
                >
                  <Icon
                    name={getClassificationIcon(
                      permit.regbl_classification_general
                    )}
                    size="small"
                  />
                  {t(
                    `buildingPermits.filterOptions.regblClassification.${permit.regbl_classification_general.replace(/ /g, '_')}`
                  )}
                </S.SummaryChip>
              )}

              {/* Development Project */}
              {permit.development_classification_final && (
                <S.SummaryChip
                  $variant="success"
                  onClick={() => scrollToSection(classificationRef)}
                >
                  <Icon name="hard-hat" size="small" />
                  {t('buildingPermits.detail.chips.devProject')}
                </S.SummaryChip>
              )}

              {/* Land Area */}
              {landArea && landArea > 0 && (
                <S.SummaryChip onClick={() => scrollToSection(landParcelsRef)}>
                  <Icon name="ruler" size="small" />
                  {formatLandArea
                    ? formatLandArea(landArea)
                    : `${landArea.toLocaleString('de-CH')} m²`}
                </S.SummaryChip>
              )}

              {/* Parcels Count */}
              {permit.lands && permit.lands.length > 0 && (
                <S.SummaryChip onClick={() => scrollToSection(landParcelsRef)}>
                  <Icon name="map" size="small" />
                  {t('buildingPermits.detail.chips.parcels', {
                    count: permit.lands.length,
                  })}
                </S.SummaryChip>
              )}

              {/* Participants Count */}
              {participantsCount > 0 && (
                <S.SummaryChip onClick={() => scrollToSection(participantsRef)}>
                  <Icon name="users" size="small" />
                  {t('buildingPermits.detail.chips.participants', {
                    count: participantsCount,
                  })}
                </S.SummaryChip>
              )}

              {/* Features Count */}
              {featuresCount > 0 && (
                <S.SummaryChip onClick={() => scrollToSection(featuresRef)}>
                  <Icon name="wrench" size="small" />
                  {t('buildingPermits.detail.chips.features', {
                    count: featuresCount,
                  })}
                </S.SummaryChip>
              )}
            </S.QuickSummaryChips>
          </S.PermitInfoContent>

          {/* Map on right side (hidden in print mode) */}
          {!printMode && permit?.geo_center && MAPBOX_TOKEN && (
            <S.PermitMapContainer ref={mapRef} />
          )}
        </S.PermitInfoMainContainer>
      </S.PermitInfoSection>

      <S.DetailContent>
        {/* Section 1: Permit Information */}
        <S.DataGroup ref={permitInfoRef}>
          <S.DataSectionTitle>
            <Icon name="info" size="small" />
            {t('buildingPermits.detail.sections.permitInfo')}
          </S.DataSectionTitle>
          <S.DataGrid>
            <S.DataColumn>
              <S.DataRow>
                <S.DataLabel>
                  {t('buildingPermits.detail.fields.officialId')}
                </S.DataLabel>
                <S.DataValue>{officialId}</S.DataValue>
              </S.DataRow>
              {permit.fao_id && (
                <S.DataRow>
                  <S.DataLabel>
                    {t('buildingPermits.detail.fields.faoId')}
                  </S.DataLabel>
                  <S.DataValue>{permit.fao_id}</S.DataValue>
                </S.DataRow>
              )}
              <S.DataRow>
                <S.DataLabel>
                  {t('buildingPermits.detail.fields.status')}
                </S.DataLabel>
                <S.DataValue>{status && getStatusBadge(status)}</S.DataValue>
              </S.DataRow>
              {permit.language && (
                <S.DataRow>
                  <S.DataLabel>
                    {t('buildingPermits.detail.fields.language')}
                  </S.DataLabel>
                  <S.DataValue>{permit.language.toUpperCase()}</S.DataValue>
                </S.DataRow>
              )}
            </S.DataColumn>

            <S.DataColumn>
              <S.DataRow>
                <S.DataLabel>
                  {t('buildingPermits.detail.fields.inquiryStart')}
                </S.DataLabel>
                <S.DataValue>
                  {formatDate(permit.inquiry_start_date)}
                </S.DataValue>
              </S.DataRow>
              <S.DataRow>
                <S.DataLabel>
                  {t('buildingPermits.detail.fields.inquiryLast')}
                </S.DataLabel>
                <S.DataValue>
                  {formatDate(permit.inquiry_last_date)}
                </S.DataValue>
              </S.DataRow>
              <S.DataRow>
                <S.DataLabel>
                  {t('buildingPermits.detail.fields.approvalDate')}
                </S.DataLabel>
                <S.DataValue>
                  {formatDate(permit.inquiry_approval_date)}
                </S.DataValue>
              </S.DataRow>
              {permit.publication_type_name && (
                <S.DataRow>
                  <S.DataLabel>
                    {t('buildingPermits.detail.fields.publicationType')}
                  </S.DataLabel>
                  <S.DataValue>
                    {permit.publication_type_name}
                    {permit.publication_type_code &&
                      ` (${permit.publication_type_code})`}
                  </S.DataValue>
                </S.DataRow>
              )}
            </S.DataColumn>
          </S.DataGrid>
        </S.DataGroup>

        {/* Section 2: Project Description */}
        {permit.description && (
          <S.DataGroup>
            <S.DataSectionTitle>
              <Icon name="file-text" size="small" />
              {t('buildingPermits.detail.sections.projectDescription')}
            </S.DataSectionTitle>
            <S.DescriptionBox>{permit.description}</S.DescriptionBox>
          </S.DataGroup>
        )}

        {/* Section 3: Classification & Work Type */}
        <S.DataGroup ref={classificationRef}>
          <S.DataSectionTitle>
            <Icon name="tag" size="small" />
            {t('buildingPermits.detail.sections.classificationWorkType')}
          </S.DataSectionTitle>
          <S.DataGrid>
            <S.DataColumn>
              {permit.work_type_official && (
                <S.DataRow>
                  <S.DataLabel>
                    {t('buildingPermits.detail.fields.workType')}
                  </S.DataLabel>
                  <S.DataValue>{permit.work_type_official}</S.DataValue>
                </S.DataRow>
              )}
              {permit.regbl_classification_general && (
                <S.DataRow>
                  <S.DataLabel>
                    {t('buildingPermits.detail.fields.regblClassification')}
                  </S.DataLabel>
                  <S.DataValue>
                    <S.PropertyBadge
                      $type={permit.regbl_classification_general}
                    >
                      {t(
                        `buildingPermits.filterOptions.regblClassification.${permit.regbl_classification_general.replace(/ /g, '_')}`
                      )}
                    </S.PropertyBadge>
                  </S.DataValue>
                </S.DataRow>
              )}
              {permit.development_classification_final !== undefined && (
                <S.DataRow>
                  <S.DataLabel>
                    {t('buildingPermits.detail.fields.developmentPotential')}
                  </S.DataLabel>
                  <S.DataValue>
                    {permit.development_classification_final ? (
                      <Badge variant="success" size="small">
                        {t('common.yes')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" size="small">
                        {t('common.no')}
                      </Badge>
                    )}
                  </S.DataValue>
                </S.DataRow>
              )}
            </S.DataColumn>

            <S.DataColumn>
              {regblFlags.length > 0 && (
                <div>
                  <S.DataLabel style={{ marginBottom: '8px' }}>
                    {t('buildingPermits.detail.fields.regblFeatures')}
                  </S.DataLabel>
                  <S.FlagsList>
                    {regblFlags.map((flag) => (
                      <S.FlagItem key={flag.key}>
                        <Icon
                          name={regblFlagIcons[flag.key] || 'check'}
                          size="small"
                        />
                        {t(
                          `buildingPermits.filterOptions.regblBoolean.${flag.key}`
                        )}
                      </S.FlagItem>
                    ))}
                  </S.FlagsList>
                </div>
              )}
            </S.DataColumn>
          </S.DataGrid>
        </S.DataGroup>

        {/* Section 4: Participants */}
        {Object.keys(participantsByRole).length > 0 && (
          <S.DataGroup ref={participantsRef}>
            <S.DataSectionTitle>
              <Icon name="users" size="small" />
              {t('buildingPermits.detail.sections.participants', {
                count: participantsCount,
              })}
            </S.DataSectionTitle>
            <S.ParticipantsContainer>
              {/* Owner Group */}
              {participantsByRole.owner && (
                <S.ParticipantGroup>
                  <S.ParticipantGroupHeader $role="owner">
                    {t('buildingPermits.detail.participantTypes.owners')}
                    <span>{participantsByRole.owner.length}</span>
                  </S.ParticipantGroupHeader>
                  <S.ParticipantList>
                    {participantsByRole.owner.map((p, idx) => (
                      <S.ParticipantCard key={idx}>
                        <S.ParticipantType>
                          {p.type
                            ? t(
                                `buildingPermits.filterOptions.participantType.${p.type.toLowerCase()}`,
                                { defaultValue: p.type }
                              )
                            : t('common.unknown')}
                        </S.ParticipantType>
                        <S.ParticipantName>
                          {p.full_name ||
                            `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                            '—'}
                        </S.ParticipantName>
                        {p.roles && p.roles.length > 0 && (
                          <S.ParticipantRole>
                            {p.roles
                              .map((r) =>
                                t(
                                  `buildingPermits.detail.roles.${r.role?.toLowerCase()}`,
                                  { defaultValue: r.role }
                                )
                              )
                              .join(', ')}
                            {p.roles[0].role_organization &&
                              ` • ${p.roles[0].role_organization}`}
                          </S.ParticipantRole>
                        )}
                        <S.ParticipantDetails>
                          {p.address_street && <div>{p.address_street}</div>}
                          {(p.address_postal_code || p.address_city) && (
                            <div>
                              {p.address_postal_code} {p.address_city}
                            </div>
                          )}
                        </S.ParticipantDetails>
                      </S.ParticipantCard>
                    ))}
                  </S.ParticipantList>
                </S.ParticipantGroup>
              )}

              {/* Architect Group */}
              {participantsByRole.architect && (
                <S.ParticipantGroup>
                  <S.ParticipantGroupHeader $role="architect">
                    {t('buildingPermits.detail.participantTypes.architects')}
                    <span>{participantsByRole.architect.length}</span>
                  </S.ParticipantGroupHeader>
                  <S.ParticipantList>
                    {participantsByRole.architect.map((p, idx) => (
                      <S.ParticipantCard key={idx}>
                        <S.ParticipantType>
                          {p.type
                            ? t(
                                `buildingPermits.filterOptions.participantType.${p.type.toLowerCase()}`,
                                { defaultValue: p.type }
                              )
                            : t('common.unknown')}
                        </S.ParticipantType>
                        <S.ParticipantName>
                          {p.full_name ||
                            `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                            '—'}
                        </S.ParticipantName>
                        {p.roles && p.roles.length > 0 && (
                          <S.ParticipantRole>
                            {p.roles
                              .map((r) =>
                                t(
                                  `buildingPermits.detail.roles.${r.role?.toLowerCase()}`,
                                  { defaultValue: r.role }
                                )
                              )
                              .join(', ')}
                            {p.roles[0].role_organization &&
                              ` • ${p.roles[0].role_organization}`}
                          </S.ParticipantRole>
                        )}
                        <S.ParticipantDetails>
                          {p.address_street && <div>{p.address_street}</div>}
                          {(p.address_postal_code || p.address_city) && (
                            <div>
                              {p.address_postal_code} {p.address_city}
                            </div>
                          )}
                        </S.ParticipantDetails>
                      </S.ParticipantCard>
                    ))}
                  </S.ParticipantList>
                </S.ParticipantGroup>
              )}

              {/* Applicant Group */}
              {participantsByRole.applicant && (
                <S.ParticipantGroup>
                  <S.ParticipantGroupHeader $role="applicant">
                    {t('buildingPermits.detail.participantTypes.applicants')}
                    <span>{participantsByRole.applicant.length}</span>
                  </S.ParticipantGroupHeader>
                  <S.ParticipantList>
                    {participantsByRole.applicant.map((p, idx) => (
                      <S.ParticipantCard key={idx}>
                        <S.ParticipantType>
                          {p.type
                            ? t(
                                `buildingPermits.filterOptions.participantType.${p.type.toLowerCase()}`,
                                { defaultValue: p.type }
                              )
                            : t('common.unknown')}
                        </S.ParticipantType>
                        <S.ParticipantName>
                          {p.full_name ||
                            `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                            '—'}
                        </S.ParticipantName>
                        {p.roles && p.roles.length > 0 && (
                          <S.ParticipantRole>
                            {p.roles
                              .map((r) =>
                                t(
                                  `buildingPermits.detail.roles.${r.role?.toLowerCase()}`,
                                  { defaultValue: r.role }
                                )
                              )
                              .join(', ')}
                          </S.ParticipantRole>
                        )}
                        <S.ParticipantDetails>
                          {p.address_street && <div>{p.address_street}</div>}
                          {(p.address_postal_code || p.address_city) && (
                            <div>
                              {p.address_postal_code} {p.address_city}
                            </div>
                          )}
                        </S.ParticipantDetails>
                      </S.ParticipantCard>
                    ))}
                  </S.ParticipantList>
                </S.ParticipantGroup>
              )}

              {/* Other Group */}
              {participantsByRole.other && (
                <S.ParticipantGroup>
                  <S.ParticipantGroupHeader>
                    {t('buildingPermits.detail.participantTypes.other')}
                    <span>{participantsByRole.other.length}</span>
                  </S.ParticipantGroupHeader>
                  <S.ParticipantList>
                    {participantsByRole.other.map((p, idx) => (
                      <S.ParticipantCard key={idx}>
                        <S.ParticipantType>
                          {p.type
                            ? t(
                                `buildingPermits.filterOptions.participantType.${p.type.toLowerCase()}`,
                                { defaultValue: p.type }
                              )
                            : t('common.unknown')}
                        </S.ParticipantType>
                        <S.ParticipantName>
                          {p.full_name ||
                            `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                            '—'}
                        </S.ParticipantName>
                        {p.roles && p.roles.length > 0 && (
                          <S.ParticipantRole>
                            {p.roles
                              .map((r) =>
                                t(
                                  `buildingPermits.detail.roles.${r.role?.toLowerCase()}`,
                                  { defaultValue: r.role }
                                )
                              )
                              .join(', ')}
                          </S.ParticipantRole>
                        )}
                      </S.ParticipantCard>
                    ))}
                  </S.ParticipantList>
                </S.ParticipantGroup>
              )}
            </S.ParticipantsContainer>
          </S.DataGroup>
        )}

        {/* Section 5: Land Parcels */}
        {permit.lands && permit.lands.length > 0 && (
          <S.DataGroup ref={landParcelsRef}>
            <S.DataSectionTitle>
              <Icon name="grid" size="small" />
              {t('buildingPermits.detail.sections.landParcels', {
                count: permit.lands.length,
              })}
            </S.DataSectionTitle>
            {landsLoading ? (
              <S.LoadingState>
                <Loading />
              </S.LoadingState>
            ) : resolvedLands && resolvedLands.length > 0 ? (
              resolvedLands.map((land) => (
                <LandAnalysisCard
                  key={land.id}
                  landAnalysis={land}
                  onView={onViewLand}
                  translations={{
                    view: t('buildingPermits.detail.buttons.viewDetails'),
                    bldg: t('common.landCard.bldg'),
                    units: t('common.landCard.units'),
                  }}
                />
              ))
            ) : (
              permit.lands.map((land, idx) => (
                <S.PropertyCard key={idx}>
                  <S.PropertyHeader>
                    <S.PropertyHeaderLeft>
                      <S.MapMarkerBadge>{idx + 1}</S.MapMarkerBadge>
                      <span>
                        {t('buildingPermits.detail.fields.parcel')}{' '}
                        {land.code_number ||
                          land.land_register_number ||
                          land.id ||
                          `#${idx + 1}`}
                      </span>
                    </S.PropertyHeaderLeft>
                    <S.DataValue>
                      {land.area
                        ? formatLandArea
                          ? formatLandArea(land.area)
                          : `${land.area.toLocaleString('de-CH')} m²`
                        : '—'}
                    </S.DataValue>
                  </S.PropertyHeader>
                  <S.PropertyInfo>
                    <S.PropertyInfoRow>
                      <span>
                        {t('buildingPermits.detail.fields.parcelNumber')}
                      </span>
                      <span>
                        {land.code_number || land.land_register_number || '—'}
                      </span>
                    </S.PropertyInfoRow>
                    <S.PropertyInfoRow>
                      <span>{t('buildingPermits.detail.fields.area')}</span>
                      <span>
                        {land.area
                          ? `${land.area.toLocaleString('de-CH')} m²`
                          : '—'}
                      </span>
                    </S.PropertyInfoRow>
                  </S.PropertyInfo>
                </S.PropertyCard>
              ))
            )}
          </S.DataGroup>
        )}

        {/* Section 6: Scope of Work */}
        {permit.features && permit.features.length > 0 && (
          <S.DataGroup ref={featuresRef}>
            <S.DataSectionTitle>
              <Icon name="wrench" size="small" />
              {t('buildingPermits.detail.sections.scopeOfWork', {
                count: permit.features.length,
              })}
            </S.DataSectionTitle>
            <S.FeatureTable>
              <S.FeatureTableHeader>
                <div>{t('buildingPermits.detail.fields.action')}</div>
                <div>{t('buildingPermits.detail.fields.category')}</div>
                <div>{t('buildingPermits.detail.fields.subcategory')}</div>
                <div>{t('buildingPermits.detail.fields.usage')}</div>
              </S.FeatureTableHeader>
              {permit.features.map((feature, idx) => {
                const action = feature.action?.toLowerCase()
                const iconName: IconName =
                  action === 'construction'
                    ? 'building-2'
                    : action === 'transformation' || action === 'renovation'
                      ? 'wrench'
                      : action === 'demolition'
                        ? 'hammer'
                        : 'settings'

                return (
                  <S.FeatureTableRow key={idx} $even={idx % 2 === 0}>
                    <S.FeatureTableCell>
                      <S.FeatureActionIcon $action={feature.action}>
                        <Icon name={iconName} size="small" />
                      </S.FeatureActionIcon>
                      {feature.action
                        ? t(
                            `buildingPermits.detail.featureAction.${feature.action.toLowerCase()}`,
                            { defaultValue: feature.action }
                          )
                        : '—'}
                    </S.FeatureTableCell>
                    <S.FeatureTableCell>
                      {feature.category || '—'}
                    </S.FeatureTableCell>
                    <S.FeatureTableCell>
                      {feature.subcategory || '—'}
                    </S.FeatureTableCell>
                    <S.FeatureTableCell>
                      {feature.usage_category
                        ? t(
                            `buildingPermits.detail.featureUsageCategory.${feature.usage_category.toLowerCase()}`,
                            { defaultValue: feature.usage_category }
                          )
                        : '—'}
                    </S.FeatureTableCell>
                  </S.FeatureTableRow>
                )
              })}
            </S.FeatureTable>
          </S.DataGroup>
        )}

        {/* Section 7: Land Use Types */}
        {permit.land_use_principal_types &&
          permit.land_use_principal_types.length > 0 && (
            <S.DataGroup>
              <S.DataSectionTitle>
                <Icon name="layers" size="small" />
                {t('buildingPermits.detail.sections.landUseTypes')}
              </S.DataSectionTitle>
              <S.LandUseList>
                {permit.land_use_principal_types.map((landUse, idx) => (
                  <S.LandUseItem key={idx}>
                    {landUse.land_use_type || '—'}
                    {landUse.percentage !== undefined &&
                      ` (${landUse.percentage}%)`}
                  </S.LandUseItem>
                ))}
              </S.LandUseList>
            </S.DataGroup>
          )}

        {/* External Link - only show in non-print mode */}
        {permit.url_web && !printMode && (
          <S.DataGroup>
            <S.ExternalLinkButton
              href={permit.url_web}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="arrow-u-up-right" size="small" />
              {t('buildingPermits.detail.viewDocument')}
            </S.ExternalLinkButton>
          </S.DataGroup>
        )}
      </S.DetailContent>
    </PrintModeWrapper>
  )
}
